import { Component, ChangeDetectionStrategy, input, signal, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'widget-lottie',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="lottie-kapsul" [style.width.px]="genislik()" [style.height.px]="yukseklik()">
      @if (hata()) {
        <div class="placeholder">
          <span class="emoji">&#127916;</span>
          <span class="baslik">Animasyon</span>
          @if (animasyonUrl()) {
            <a class="link" [href]="animasyonUrl()" target="_blank" rel="noopener">Kaynagi ac</a>
          }
        </div>
      } @else if (yukleniyor()) {
        <div class="placeholder">
          <div class="spinner"></div>
        </div>
      } @else {
        <svg class="lottie-svg"
             [attr.viewBox]="viewBox()"
             [attr.width]="genislik()"
             [attr.height]="yukseklik()">
          @for (katman of kareler()[mevcutKare()]; track $index) {
            <path [attr.d]="katman.d" [attr.fill]="katman.fill" [attr.opacity]="katman.opacity" />
          }
        </svg>
      }
    </div>
  `,
  styles: [`
    :host { display: inline-block; }
    .lottie-kapsul {
      display: flex; align-items: center; justify-content: center;
      border-radius: 8px; background: var(--py-hover, rgba(0,0,0,0.02)); overflow: hidden;
    }
    .placeholder {
      display: flex; flex-direction: column; align-items: center; gap: 6px;
    }
    .emoji { font-size: 32px; line-height: 1; }
    .baslik { font-size: 12px; color: var(--py-metin-ikincil, #8c8c8c); font-weight: 500; }
    .link {
      font-size: 11px; color: #1890ff; text-decoration: none;
      max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .link:hover { text-decoration: underline; }
    .lottie-svg { display: block; }
    .spinner {
      width: 22px; height: 22px; border-radius: 50%;
      border: 2px solid var(--py-sinir, #d9d9d9);
      border-top-color: #1890ff;
      animation: don 0.8s linear infinite;
    }
    @keyframes don { to { transform: rotate(360deg); } }
  `],
})
export class LottieComponent implements AfterViewInit, OnDestroy {
  animasyonUrl = input('');
  genislik = input(120);
  yukseklik = input(120);
  dongu = input(true);
  otomatikBaslat = input(true);

  yukleniyor = signal(true);
  hata = signal(false);
  kareler = signal<IKare[][]>([]);
  mevcutKare = signal(0);
  viewBox = signal('0 0 120 120');

  private animFrameId = 0;
  private baslangicZamani = 0;
  private abortCtrl: AbortController | null = null;
  private fps = 30;
  private toplamKare = 0;

  ngAfterViewInit() {
    const url = this.animasyonUrl();
    if (!url) { this.hata.set(true); this.yukleniyor.set(false); return; }
    this.yukle(url);
  }

  ngOnDestroy() {
    if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
    this.abortCtrl?.abort();
  }

  private async yukle(url: string) {
    try {
      this.abortCtrl = new AbortController();
      const res = await fetch(url, { signal: this.abortCtrl.signal });
      if (!res.ok) throw new Error();
      const json = await res.json();
      this.parseEt(json);
    } catch {
      this.hata.set(true);
      this.yukleniyor.set(false);
    }
  }

  private parseEt(json: any) {
    const w = json.w || 120;
    const h = json.h || 120;
    this.fps = json.fr || 30;
    const ip = json.ip || 0;
    const op = json.op || 60;
    this.toplamKare = Math.max(1, Math.floor(op - ip));
    this.viewBox.set(`0 0 ${w} ${h}`);

    const katmanlar: IKare[][] = [];
    for (let f = 0; f < this.toplamKare; f++) {
      katmanlar.push(this.kareOlustur(json.layers || [], f + ip));
    }

    this.kareler.set(katmanlar);
    this.yukleniyor.set(false);

    if (katmanlar.length === 0 || katmanlar.every(k => k.length === 0)) {
      this.hata.set(true);
      return;
    }

    if (this.otomatikBaslat()) this.oynat();
  }

  private kareOlustur(layers: any[], kare: number): IKare[] {
    const sonuc: IKare[] = [];
    for (const lay of layers) {
      if (lay.ty !== 4 || !lay.shapes) continue;
      const ip = lay.ip ?? 0;
      const op = lay.op ?? Infinity;
      if (kare < ip || kare >= op) continue;
      this.shapeParseEt(lay.shapes, sonuc, kare);
    }
    return sonuc;
  }

  private shapeParseEt(shapes: any[], sonuc: IKare[], kare: number) {
    for (const s of shapes) {
      if (s.ty === 'gr' && s.it) {
        this.shapeParseEt(s.it, sonuc, kare);
      } else if (s.ty === 'sh' && s.ks) {
        const d = this.pathCikar(s.ks, kare);
        if (d) sonuc.push({ d, fill: '', opacity: 1 });
      } else if (s.ty === 'fl' && s.c) {
        const renk = this.renkCikar(s.c, kare);
        if (renk && sonuc.length > 0) {
          const son = sonuc[sonuc.length - 1];
          if (!son.fill) son.fill = renk;
        }
        if (s.o) {
          const o = this.degerCikar(s.o, kare);
          if (o !== undefined && sonuc.length > 0) {
            sonuc[sonuc.length - 1].opacity = (o as number) / 100;
          }
        }
      }
    }
  }

  private pathCikar(ks: any, kare: number): string {
    const veri = ks.a === 1 ? this.animDegerBul(ks.k, kare) : ks.k;
    if (!veri || !veri.v) return '';
    const { v, i: iArr, o: oArr, c } = veri;
    if (!v.length) return '';

    let d = `M${v[0][0]},${v[0][1]}`;
    for (let j = 1; j < v.length; j++) {
      const p = j - 1;
      const cpX1 = v[p][0] + (oArr[p]?.[0] || 0);
      const cpY1 = v[p][1] + (oArr[p]?.[1] || 0);
      const cpX2 = v[j][0] + (iArr[j]?.[0] || 0);
      const cpY2 = v[j][1] + (iArr[j]?.[1] || 0);
      d += `C${cpX1},${cpY1} ${cpX2},${cpY2} ${v[j][0]},${v[j][1]}`;
    }
    if (c) {
      const son = v.length - 1;
      const cpX1 = v[son][0] + (oArr[son]?.[0] || 0);
      const cpY1 = v[son][1] + (oArr[son]?.[1] || 0);
      const cpX2 = v[0][0] + (iArr[0]?.[0] || 0);
      const cpY2 = v[0][1] + (iArr[0]?.[1] || 0);
      d += `C${cpX1},${cpY1} ${cpX2},${cpY2} ${v[0][0]},${v[0][1]}Z`;
    }
    return d;
  }

  private renkCikar(c: any, kare: number): string {
    const val = c.a === 1 ? this.animDegerBul(c.k, kare) : c.k;
    if (!val || !Array.isArray(val)) return '#888';
    const r = Math.round((val[0] || 0) * 255);
    const g = Math.round((val[1] || 0) * 255);
    const b = Math.round((val[2] || 0) * 255);
    return `rgb(${r},${g},${b})`;
  }

  private degerCikar(prop: any, kare: number): number | undefined {
    if (!prop) return undefined;
    const val = prop.a === 1 ? this.animDegerBul(prop.k, kare) : prop.k;
    return typeof val === 'number' ? val : Array.isArray(val) ? val[0] : undefined;
  }

  private animDegerBul(keyframes: any[], kare: number): any {
    if (!Array.isArray(keyframes) || keyframes.length === 0) return null;
    for (let i = keyframes.length - 1; i >= 0; i--) {
      if (keyframes[i].t !== undefined && kare >= keyframes[i].t) {
        return keyframes[i].s?.[0] ?? keyframes[i].s ?? keyframes[i].e?.[0] ?? null;
      }
    }
    return keyframes[0]?.s?.[0] ?? keyframes[0]?.s ?? null;
  }

  private oynat() {
    this.baslangicZamani = performance.now();
    const adim = (zaman: number) => {
      const gecen = zaman - this.baslangicZamani;
      const kareIdx = Math.floor((gecen / 1000) * this.fps) % this.toplamKare;
      this.mevcutKare.set(kareIdx);

      if (!this.dongu() && kareIdx >= this.toplamKare - 1) return;
      this.animFrameId = requestAnimationFrame(adim);
    };
    this.animFrameId = requestAnimationFrame(adim);
  }
}

interface IKare {
  d: string;
  fill: string;
  opacity: number;
}
